import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';
import { EuroFormat } from '../../../components/common/inputFormats';
import Bestaetigungsdialog from '../../../components/dialog/Bestaetigungsdialog';
import { GUTACHTENARTEN_TEXTE } from '../../../shared/domain/vorgang';
import { Mandant, RechnungsparameterEintrag } from '../../../types';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { RECHNUNGSPOSITION_AUSWAHL, RECHNUNGSPOSITION_EINHEIT_AUSWAHL, RechnungspositionEinheit } from '../../../shared/constants';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import { ermittleRechnungspositionLabel } from '../../../shared/rechnungHelper';

const addButton = {
  display: 'flex',
  marginTop: '1rem',
  justifyContent: 'center'
};

const filterOptions = createFilterOptions<string>({
  matchFrom: 'start',
  trim: true
});

const auswahl: string[] = RECHNUNGSPOSITION_AUSWAHL.map((p) => p.label);

const ermittleValueZuLabel = (label: string | null) => {
  return RECHNUNGSPOSITION_AUSWAHL.find((rp) => rp.label === label)?.value ?? label;
};

type Props = {
  readonly isLoading: boolean;
  readonly mandant: Mandant;
  readonly speichereMandant: (mandant: Partial<Mandant>) => void;
};

export function Rechnungsparameter({ isLoading, mandant, speichereMandant }: Props) {
  const [rechnungsparameter, setRechnungsparameter] = useState<RechnungsparameterEintrag[]>(
    mandant.rechnungsparameter ?? ([] as Array<RechnungsparameterEintrag>)
  );

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const [loeschenIndex, setLoeschenIndex] = useState<number>(-1);

  useEffect(() => {
    setRechnungsparameter(mandant.rechnungsparameter ?? []);
  }, [mandant]);

  const fuegeNeueParameterHinzu = () => {
    setRechnungsparameter((state) => [
      ...state,
      {
        titel: '',
        einheit: RechnungspositionEinheit.EURO,
        wert: 0,
        gutachtenart: []
      }
    ]);
  };

  const aktualisiereParameter = (index: number, attribute: string, wert: number | string | string[] | null) => {
    const rechnungsparameterUpdate = rechnungsparameter.map((r, i) => {
      return index === i ? { ...r, [attribute]: wert } : r;
    });
    setRechnungsparameter(rechnungsparameterUpdate);
  };

  const loescheEintrag = (index: number) => {
    const neueRechnungsparameter = rechnungsparameter.filter((r, i) => i !== index);
    setRechnungsparameter(neueRechnungsparameter);
    setLoeschenIndex(-1);
    speichereMandant({
      rechnungsparameter: neueRechnungsparameter
    });
  };

  const sensors = useSensors(useSensor(PointerSensor));

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = parseInt(String(active.id));
      const newIndex = parseInt(String(over.id));
      setRechnungsparameter(arrayMove(rechnungsparameter, oldIndex, newIndex));
    }
  };

  return (
    <>
      <Accordion data-testid={'rechnungsParameterAccordion'}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" gutterBottom>
            Rechnungspositionen
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container>
            <Grid size={12}>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Rechnungsposition</TableCell>
                      <TableCell>Einheit</TableCell>
                      <TableCell>Wert</TableCell>
                      <TableCell>Gutachtenart</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                    <SortableContext items={rechnungsparameter.map((_, i) => String(i))} strategy={verticalListSortingStrategy}>
                      <TableBody>
                        {rechnungsparameter.map((position, index) => (
                          <RechnungsparamterZeile
                            key={index}
                            position={index}
                            parameter={position}
                            aktualisiereParameter={aktualisiereParameter}
                            setLoeschenIndex={setLoeschenIndex}
                            setOpenDeleteDialog={setOpenDeleteDialog}
                          />
                        ))}
                      </TableBody>
                    </SortableContext>
                  </DndContext>
                </Table>
              </TableContainer>
            </Grid>
            <Grid size={12} sx={addButton}>
              <IconButton color="primary" onClick={fuegeNeueParameterHinzu} data-testid="hinzufuegen" size="large">
                <AddIcon />
              </IconButton>
            </Grid>
            <Grid size={12} sx={addButton}>
              <Button
                color="primary"
                variant="contained"
                disabled={isLoading}
                onClick={() =>
                  speichereMandant({
                    rechnungsparameter
                  })
                }
                data-testid="submit"
              >
                Speichern
              </Button>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      <Bestaetigungsdialog
        title="Eintrag löschen"
        text="Möchten Sie den Eintrag wirklich löschen?"
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onDelete={() => {
          loescheEintrag(loeschenIndex);
          setOpenDeleteDialog(false);
        }}
      />
    </>
  );
}

type RechnungsparameterZeileProps = {
  position: number;
  parameter: RechnungsparameterEintrag;
  aktualisiereParameter: (index: number, attribute: string, value: string | number | string[] | null) => void;
  setLoeschenIndex: (index: number) => void;
  setOpenDeleteDialog: (open: boolean) => void;
};

function RechnungsparamterZeile({ position, parameter, aktualisiereParameter, setLoeschenIndex, setOpenDeleteDialog }: RechnungsparameterZeileProps) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: String(position) });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <TableRow ref={setNodeRef} style={style} data-testid={`entry-${position}`}>
      <TableCell>
        <Autocomplete
          disabled={false}
          filterOptions={filterOptions}
          freeSolo
          options={auswahl}
          value={ermittleRechnungspositionLabel(parameter.titel)}
          onChange={(event, value: string | null) => {
            aktualisiereParameter(position, 'titel', ermittleValueZuLabel(value));
          }}
          renderInput={(params) => (
            <TextField
              variant="standard"
              {...params}
              data-testid="Rechnungsparameter-position"
              InputProps={{
                ...params.InputProps
              }}
              onChange={(event) => {
                aktualisiereParameter(position, 'titel', ermittleValueZuLabel(event.target.value));
              }}
            />
          )}
        />
      </TableCell>
      <TableCell>
        <Select
          variant="standard"
          label="Einheit"
          value={parameter.einheit}
          disabled={false}
          onChange={(event: SelectChangeEvent<string>) => {
            aktualisiereParameter(position, 'einheit', event.target.value);
          }}
          data-testid="Rechnungsparameter-einheit"
        >
          {RECHNUNGSPOSITION_EINHEIT_AUSWAHL.map((einheit) => (
            <MenuItem key={einheit.value} value={einheit.value} data-testid={einheit.label}>
              {einheit.label}
            </MenuItem>
          ))}
        </Select>
      </TableCell>
      <TableCell>
        <TextField
          fullWidth
          variant="standard"
          value={parameter.wert / 100}
          data-testid="Rechnungsparameter-wert"
          InputProps={{
            inputComponent: EuroFormat,
            endAdornment: <InputAdornment position="end">€</InputAdornment>
          }}
          onChange={(event) => {
            aktualisiereParameter(position, 'wert', (parseFloat(event.target.value) || 0) * 100);
          }}
        />
      </TableCell>
      <TableCell>
        <Select<string[]>
          fullWidth
          variant="standard"
          multiple
          value={parameter.gutachtenart}
          disabled={false}
          onChange={(event: SelectChangeEvent<string[]>) => {
            aktualisiereParameter(position, 'gutachtenart', event.target.value);
          }}
          data-testid="Rechnungsparameter-gutachtenart"
        >
          {Object.entries(GUTACHTENARTEN_TEXTE).map((g) => (
            <MenuItem key={g[0]} value={g[0]} data-testid={`${g[0]}`}>
              {g[1]}
            </MenuItem>
          ))}
        </Select>
      </TableCell>
      <TableCell>
        <IconButton
          onClick={() => {
            setOpenDeleteDialog(true);
            setLoeschenIndex(position);
          }}
          data-testid="Rechnungsparameter-eintrag-loeschen"
        >
          <DeleteIcon />
        </IconButton>
        <IconButton sx={{ cursor: 'grab' }} {...attributes} {...listeners}>
          <DragIndicatorIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}
